const Shortvas = require("shortvas");
const EventEmitter = require("events");
const makeMockContext = require("../../../shared/makeMockContext");

const canvasCountTracker = {};

const createCanvas = function(width, height, options) {
	if (!global.document) {
		return;
	}
	const {source = "<unknown>"} = options || {};

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;

	canvasCountTracker[source] = (canvasCountTracker[source] || 0) + 1;

	return canvas;
};

// See https://bugs.aops.com/T11847 for more info. You can't actually manually
// free memory in Javascript, but resetting the canvas size really helped
// Safari out when we were trying things in jsfiddle.
const freeCanvasMemory = function(canvas, options) {
	const {source = null} = options || {};

	if (canvas.width > 1 || canvas.height > 1) {
		canvasCountTracker[source] = (canvasCountTracker[source] || 0) - 1;
	}

	canvas.width = 1;
	canvas.height = 1;
};

// Call this if you reuse a previously freed canvas.
const incrementCanvasCount = function(options) {
	const {source = null} = options || {};

	canvasCountTracker[source] = (canvasCountTracker[source] || 0) + 1;
};

const getCanvasCountInfo = function() {
	return canvasCountTracker;
};

const nullContextMonitor = new EventEmitter();
const addNullContextListener = function(cb) {
	nullContextMonitor.addListener("nullContext", cb);
};
const removeNullContextListener = function(cb) {
	nullContextMonitor.removeListener("nullContext", cb);
};

// A thin wrapper around Shortvas.get to deal with null returns. See T13060.
const nullShortvasMessage = `Shortvas.get: Calling getContext("2d") on passed canvas returned`;
const getShortvas = function(canvasOrContext) {
	let context = null;
	try {
		context = Shortvas.get(canvasOrContext);
	} catch (ex) {
		if (ex.message.indexOf(nullShortvasMessage) > -1) {
			context = null;
		} else {
			throw ex;
		}
	}
	if (!context) {
		nullContextMonitor.emit("nullContext");
		const rawContext = makeMockContext();
		return Shortvas.get(rawContext);
	}
	return context;
};

const resetContext = function(canvas) {
	const context = getShortvas(canvas);
	setCanvasScaleTransform(context, getDPR(context));
	context.clearRect(0, 0, context.getWidth(), context.getHeight());
	context.beginPath();
};

const transientCanvas = createCanvas(100, 100, {source: "transient"});
const getTransientContext = function() {
	if (!transientCanvas) {
		return getShortvas(makeMockContext());
	}
	resetContext(transientCanvas);
	return getShortvas(transientCanvas);
};

// In addition to scaling by the specified pixel ratio, resets the transform.
const setCanvasScaleTransform = function(context, scaleFactor) {
	context.setTransform(scaleFactor, 0, 0, scaleFactor, 0, 0);
};

//fix from: http://stackoverflow.com/questions/15661339/how-do-i-fix-blurry-text-in-my-html5-canvas
const setCanvasDPI = function(canvas) {
	const context = getShortvas(canvas);
	const canvasW = canvas.width;
	const canvasH = canvas.height;
	const pixelRatio = getDPR(context);
	if (pixelRatio !== 1) {
		canvas.width = canvasW * pixelRatio;
		canvas.height = canvasH * pixelRatio;
		canvas.style.width = canvasW + "px";
		canvas.style.height = canvasH + "px";
		setCanvasScaleTransform(context, pixelRatio);
	} else {
		canvas.width = canvasW;
		canvas.height = canvasH;
		canvas.style.width = "";
		canvas.style.height = "";
		setCanvasScaleTransform(context, 1);
	}
};

const setWidth = function(canvas, w) {
	const context = getShortvas(canvas);
	const pixelRatio = getDPR(context);
	if (pixelRatio !== 1) {
		canvas.width = w * pixelRatio;
		canvas.style.width = w + "px";
		setCanvasScaleTransform(context, pixelRatio);
	} else {
		canvas.width = w;
		canvas.style.width = "";
		setCanvasScaleTransform(context, 1);
	}
};

const setHeight = function(canvas, h) {
	const context = getShortvas(canvas);
	const pixelRatio = getDPR(context);
	if (pixelRatio !== 1) {
		canvas.height = h * pixelRatio;
		canvas.style.height = h + "px";
		setCanvasScaleTransform(context, pixelRatio);
	} else {
		canvas.height = h;
		canvas.style.height = "";
		setCanvasScaleTransform(context, 1);
	}
};

Shortvas.extend({
	getWidth: function() {
		return this.canvas.width / getDPR(this.context);
	},
	getHeight: function() {
		return this.canvas.height / getDPR(this.context);
	},
});

let dprEnabled = true;
const setDPREnabled = function(val) {
	dprEnabled = val === undefined || !!val;
};

const getBSR = function(context) {
	return (
		context.webkitBackingStorePixelRatio ||
		context.mozBackingStorePixelRatio ||
		context.msBackingStorePixelRatio ||
		context.oBackingStorePixelRatio ||
		context.backingStorePixelRatio ||
		1
	);
};
let bsrCache = null;
const getDPR = function(context) {
	if (!global.window) {
		return 1;
	}
	const dpr = dprEnabled ? window.devicePixelRatio || 1 : 1;
	bsrCache = bsrCache || getBSR(context || getTransientContext());
	return dpr / bsrCache;
};

const dprMonitor = new EventEmitter();
dprMonitor.setMaxListeners(100);

const addDPRListener = function(cb) {
	dprMonitor.addListener("change", cb);
};

const removeDPRListener = function(cb) {
	dprMonitor.removeListener("change", cb);
};

// Emit change events from dprMonitor when the DPR changes.
(function() {
	if (!global.window || !global.document) {
		return;
	}
	const context = getTransientContext();
	let lastDPR = getDPR(context);
	const check = function() {
		const newDPR = getDPR(context);
		if (newDPR !== lastDPR) {
			lastDPR = newDPR;
			dprMonitor.emit("change");
		}
	};
	// Browser zoom can change DPR; add a listener to check for that immediately.
	window.addEventListener("resize", check);
	// Check periodically in case window is moved from lodpi screen to hidpi.
	setInterval(check, 500);
})();

const getOptimalLineWidth = function(context) {
	return context.measureText("|").width / 4;
};

const copyContextProperties = function(oldContext, newContext) {
	newContext.fillStyle = oldContext.fillStyle;
	newContext.strokeStyle = oldContext.strokeStyle;
	newContext.shadowColor = oldContext.shadowColor;
	newContext.setShadowBlur(oldContext.shadowBlur, {noAdjustment: true});
	newContext.shadowOffsetX = oldContext.shadowOffsetX;
	newContext.shadowOffsetY = oldContext.shadowOffsetY;
	newContext.lineCap = oldContext.lineCap;
	newContext.lineJoin = oldContext.lineJoin;
	newContext.miterLimit = oldContext.miterLimit;
	newContext.font = oldContext.font;
	newContext.textAlign = oldContext.textAlign;
	newContext.textBaseline = oldContext.textBaseline;
	newContext.globalAlpha = oldContext.globalAlpha;
	newContext.globalCompositeOperation = oldContext.globalCompositeOperation;
};

module.exports = {
	createCanvas,
	freeCanvasMemory,
	incrementCanvasCount,
	getCanvasCountInfo,
	getShortvas,
	addNullContextListener,
	removeNullContextListener,
	resetContext,
	getTransientContext,
	setCanvasScaleTransform,
	setCanvasDPI,
	setHeight,
	setWidth,
	setDPREnabled,
	getDPR,
	addDPRListener,
	removeDPRListener,
	getOptimalLineWidth,
	copyContextProperties,
};
